// Project Workspace — the per-project tabbed shell (§3): Sources · Design · Diagrams · Build · Test
// (Open Questions is a collapsible section inside the Design tab). Decomposed (P3): the panels live in
// panels/, the shared job loop in hooks/useJobPoll.js, small shared pieces in components/ and
// utils/format.js — this file owns only routing-shell concerns: loading the project + Design Model
// and handing panels a NON-DESTRUCTIVE refresh.
//
// P0.3 — two load paths, deliberately different:
//   - initial load: full-page spinner (there is nothing to show yet);
//   - background refresh (`onChanged` given to every panel): swaps project/designModel state in place,
//     NO spinner, NO unmount — so a terminal job result, its cost, the revision diff, and a failure
//     error stay on screen, and answering an open question no longer bounces the whole workspace.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Button, Group, Stack, Badge, Tabs, Loader, Center, Alert,
} from '@mantine/core';
import {
  IconArrowLeft, IconAlertCircle, IconFiles, IconLayoutGrid, IconSitemap,
  IconBuildingFactory2, IconTestPipe, IconLock,
} from '@tabler/icons-react';

import { useAuth } from '../../../shared/contexts/AuthContext';
import { getProject, getDesignModel } from './api_scripts/solnStudioClient';
import { FLOW_TYPES } from './flowCatalog';
import SourcesPanel from './panels/SourcesPanel';
import DesignPanel from './panels/DesignPanel';
import DiagramsPanel from './panels/DiagramsPanel';
import GenerationPanel from './panels/GenerationPanel';

const PHASE_COLORS = { DESIGN: 'orange', BUILD: 'blue', TEST: 'green' };

function ProjectWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getApiToken } = useAuth();

  const [project, setProject] = useState(null);
  const [designModel, setDesignModel] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('sources');
  const refreshingRef = useRef(false); // collapse overlapping background refreshes

  const load = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setInitialLoading(true);
      setError(null);
    } else {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
    }
    try {
      const proj = await getProject(id, getApiToken);
      setProject(proj);
      try {
        const dm = await getDesignModel(id, getApiToken);
        setDesignModel(dm.designModel);
      } catch {
        /* design model may not exist yet on older rows */
      }
    } catch (err) {
      // A failed BACKGROUND refresh keeps showing the data we have (transient blips are common while
      // a job churns); only the initial load surfaces a full-page error.
      if (!background) setError(err.message);
    } finally {
      if (background) refreshingRef.current = false;
      else setInitialLoading(false);
    }
  }, [id, getApiToken]);

  // Panels call this after every mutation / terminal job poll — it must never unmount them (P0.3).
  const refresh = useCallback(() => load({ background: true }), [load]);

  useEffect(() => { load(); }, [load]);

  if (initialLoading) return <Center py="xl"><Loader color="orange" /></Center>;
  if (error) {
    return (
      <Container size="lg" py="xl">
        <Button variant="subtle" color="orange" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('')}>
          Library
        </Button>
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Couldn't load project" mt="md">{error}</Alert>
      </Container>
    );
  }

  const locked = project.lockedBy && (!project.lockExpiresAt || project.lockExpiresAt > Date.now());

  return (
    <Container size="lg" py="xl">
      <Group justify="space-between" mb="md">
        <Button variant="subtle" color="orange" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('..')}>
          Library
        </Button>
        <Group gap="xs">
          <Badge color={PHASE_COLORS[project.phase] || 'gray'} variant="light">{project.phase}</Badge>
          <Badge variant="outline" color="gray">{project.status}</Badge>
          {locked && <Badge color="red" variant="light" leftSection={<IconLock size={12} />}>generating</Badge>}
        </Group>
      </Group>

      <Stack gap={2} mb="lg">
        <Title order={2}>{project.name}</Title>
        <Text c="dimmed">{project.customer} · v{project.version} · created by {project.createdBy}</Text>
      </Stack>

      <Tabs value={tab} onChange={setTab} color="orange" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="sources" leftSection={<IconFiles size={16} />}>Sources</Tabs.Tab>
          <Tabs.Tab value="design" leftSection={<IconLayoutGrid size={16} />}>Design</Tabs.Tab>
          <Tabs.Tab value="diagrams" leftSection={<IconSitemap size={16} />}>Diagrams</Tabs.Tab>
          <Tabs.Tab value="build" leftSection={<IconBuildingFactory2 size={16} />}>Build</Tabs.Tab>
          <Tabs.Tab value="test" leftSection={<IconTestPipe size={16} />}>Test</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="sources" pt="md">
          <SourcesPanel project={project} designModel={designModel} getApiToken={getApiToken} onChanged={refresh} locked={locked} />
        </Tabs.Panel>

        <Tabs.Panel value="design" pt="md">
          <DesignPanel
            project={project}
            designModel={designModel}
            getApiToken={getApiToken}
            onChanged={refresh}
            locked={locked}
          />
        </Tabs.Panel>

        <Tabs.Panel value="diagrams" pt="md">
          <DiagramsPanel
            project={project}
            designModel={designModel}
            getApiToken={getApiToken}
            onChanged={refresh}
            locked={locked}
            onEdit={(flowId) => navigate(`diagram/${flowId}`)}
          />
        </Tabs.Panel>

        <Tabs.Panel value="build" pt="md">
          <GenerationPanel
            project={project}
            designModel={designModel}
            getApiToken={getApiToken}
            onChanged={refresh}
            kind="build"
            title="Build — customer-owned CDK + importable flows"
            blurb="Scaffold-and-fill: deterministic Lex projection + flow-JSON synthesis + a ready-to-clone CDK project (§10)."
            cta="Generate build"
            artifactTypes={['CDK_PROJECT', ...FLOW_TYPES]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="test" pt="md">
          <GenerationPanel
            project={project}
            getApiToken={getApiToken}
            onChanged={refresh}
            kind="test"
            title="Test — API test-data + UAT workbooks + SIT hand-off"
            blurb="Branch enumeration over the Flow DSL → UAT plan + API test-data; thin hand-off to SIT for auto-dialing (§11)."
            cta="Generate test artifacts"
            artifactTypes={['UAT_PLAN', 'API_TEST_DATA', 'SIT_PLANS']}
          />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

export default ProjectWorkspace;
